import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, ArrowUpRight, ShieldCheck, Sparkles } from 'lucide-react';

// Editorial Intelligence Briefing
// Renders structured editorial content with last-updated timestamp.
// Designed for: CMS migration, AI-assisted generation, dynamic timestamps, SEO + Article schema.
//
// Props:
// - briefing: { intro: [], insights: [{title, body}], buyerGuidance: [], trustNote, updated }
// - kicker: short overline label
// - title: section heading
// - subjectName: subject string for cross-link CTAs (e.g. "Portugal" or "Streetwear Manufacturers")
// - relatedGuides: [{slug, title, categoryLabel}]

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
  } catch {
    return iso?.toUpperCase() || '';
  }
};

const EditorialBriefing = ({ briefing, kicker, title, subjectName, relatedGuides = [] }) => {
  if (!briefing) return null;

  return (
    <section data-testid="editorial-briefing" className="border-b border-black bg-white">
      <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
        <div className="grid gap-10 md:grid-cols-12">
          {/* Left: header */}
          <div className="md:col-span-4">
            <span className="label-overline">{kicker}</span>
            <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
              {title}
            </h2>
            <div className="mt-5 flex items-center gap-2 font-mono-data text-xs text-neutral-500" data-testid="editorial-last-updated">
              <CalendarClock size={12} strokeWidth={1.5} />
              Last updated · {formatDate(briefing.updated)}
            </div>
            <div className="mt-3 font-mono-data text-[10px] text-neutral-400">
              GIWAHS EDITORIAL INTELLIGENCE
            </div>
          </div>

          {/* Right: content */}
          <div className="md:col-span-8">
            {/* Intro paragraphs */}
            <div className="space-y-5" data-testid="editorial-intro">
              {briefing.intro.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-neutral-800 md:text-lg" style={{ fontWeight: i === 0 ? 500 : 400 }}>
                  {p}
                </p>
              ))}
            </div>

            {/* Key insights */}
            <div className="mt-10 border-t border-neutral-200 pt-8">
              <div className="label-overline text-neutral-500">Key sourcing insights</div>
              <div className="mt-5 grid gap-px bg-black sm:grid-cols-3" data-testid="editorial-insights">
                {briefing.insights.map((s, i) => (
                  <div key={i} className="bg-white p-5" data-testid={`editorial-insight-${i}`}>
                    <span className="marker-num text-neutral-500">/ {String(i + 1).padStart(2, '0')}</span>
                    <div className="mt-4 font-display text-base leading-tight tracking-tight md:text-lg" style={{ fontWeight: 600 }}>{s.title}</div>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-700">{s.body}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Buyer guidance */}
            <div className="mt-10 border-t border-neutral-200 pt-8">
              <div className="flex items-center gap-2 label-overline text-neutral-500">
                <Sparkles size={12} strokeWidth={1.5} /> Buyer-focused guidance
              </div>
              <ul className="mt-5 space-y-3" data-testid="editorial-guidance">
                {briefing.buyerGuidance.map((g, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-neutral-800 md:text-base" data-testid={`editorial-guidance-${i}`}>
                    <span className="mt-2 inline-block h-px w-4 shrink-0 bg-black" />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Trust & verification */}
            {briefing.trustNote && (
              <div className="mt-10 border border-black p-5 md:p-6" data-testid="editorial-trust-note">
                <div className="flex items-start gap-3">
                  <ShieldCheck size={18} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                  <div>
                    <div className="label-overline">Trust & Verification</div>
                    <p className="mt-3 text-sm leading-relaxed text-neutral-800">{briefing.trustNote}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Cross-links */}
            <div className="mt-10 border-t border-neutral-200 pt-8">
              <div className="label-overline text-neutral-500">Continue reading</div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {relatedGuides.slice(0, 3).map(g => (
                  <Link
                    key={g.slug}
                    to={`/sourcing-guides/${g.slug}`}
                    data-testid={`editorial-guide-${g.slug}`}
                    className="border border-black p-4 hover:bg-neutral-50"
                  >
                    <span className="label-overline text-neutral-500">{g.categoryLabel}</span>
                    <div className="mt-2 font-display text-sm tracking-tight" style={{ fontWeight: 600 }}>{g.title}</div>
                    <div className="mt-3 inline-flex items-center gap-1 label-overline">Read <ArrowUpRight size={11} strokeWidth={1.5} /></div>
                  </Link>
                ))}
                <Link to="/rfq" data-testid="editorial-rfq-cta" className="border border-black bg-black p-4 text-white hover:bg-white hover:text-black">
                  <span className="label-overline text-neutral-400">Ready to source</span>
                  <div className="mt-2 font-display text-sm tracking-tight" style={{ fontWeight: 600 }}>Submit an RFQ for {subjectName}</div>
                  <div className="mt-3 inline-flex items-center gap-1 label-overline">Build RFQ <ArrowUpRight size={11} strokeWidth={1.5} /></div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EditorialBriefing;
